import React from 'react';
import { useLocation } from '@app/utils';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Container,
  InnerContainer,
  Heading,
  Row,
  RowItem,
  ErrorMessage,
  Footer,
  ButtonContainer,
  SavingIndicator,
  IntroText,
  Loader,
} from './process-account-components';
import { AuthContext } from '@app/providers';
import { useGetUserQuery, useProcessAccountQuery } from '@app/graphql';
import { Password, Button, Icon } from '@app/components';
import { createPassword } from '@app/auth/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { getDefaultUrl } from '@app/routes/routes-helper';
import DefaultErrorPage from '@app/components/error-pages/default-error-page';

type Props = {
  firebaseId: string;
};
export default function ProcessAccount() {
  const { navigate } = useLocation();
  const { firebaseId }: Props = useParams();
  const { userInfo } = React.useContext(AuthContext);
  const defaultUrl: string = getDefaultUrl(userInfo);
  const [processAccountData] = useProcessAccountQuery();
  const { register, errors, handleSubmit, watch } = useForm();
  const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d.*)[a-zA-Z0-9\S]{6,20}$/;
  const [saving, updateSaving] = React.useState(false);
  const [showErrorPage, SetShowErrorPage] = React.useState(false);
  const { loading, data } = useGetUserQuery({ firebaseId: firebaseId });

  React.useEffect(() => {
    if (userInfo) {
      navigate(defaultUrl);
    } else if (!loading && data && data.user) {
      const { processAccount = null } = data.user;
      if (!processAccount) {
        navigate('/sign-in');
      }
    }
    // eslint-disable-next-line
  }, [loading, data, userInfo]);

  function onSubmit(processAccount: any) {
    updateSaving(true);
    const {
      email,
      processAccount: { tempPassword },
    } = data.user;
    createPassword(email, tempPassword, processAccount.password)
      .then((result: any) => {
        if (result.success) {
          processAccountData({ variables: { firebaseId: firebaseId } });
          updateSaving(false);
          navigate(defaultUrl);
        } else {
          updateSaving(false);
        }
      })
      .catch((error: any) => {
        SetShowErrorPage(true);
        updateSaving(false);
      });
  }

  return (
    <>
      <Container>
        {loading ? (
          <Loader>
            <Icon name="loader" />
          </Loader>
        ) : showErrorPage ? (
          <DefaultErrorPage />
        ) : (
          <InnerContainer>
            <Heading>Create Password</Heading>
            <IntroText>Please create password to sign in</IntroText>
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <Row>
                <RowItem>
                  <Password
                    label="Create Password"
                    name="password"
                    placeholder="Enter your Password"
                    register={register({
                      required: true,
                      pattern: passwordPattern,
                    })}
                  />
                  {errors.password && errors.password.type === 'required' && (
                    <ErrorMessage>Please enter Password</ErrorMessage>
                  )}
                  {errors.password && errors.password.type === 'pattern' && (
                    <ErrorMessage>Please enter a strong password</ErrorMessage>
                  )}
                </RowItem>
              </Row>
              <Row>
                <RowItem>
                  <Password
                    label="Confirm Password"
                    name="confirmPassword"
                    placeholder="Confirm your password"
                    register={register({
                      required: true,
                      validate: value => {
                        return value === watch('password');
                      },
                    })}
                  />
                  {errors.confirmPassword &&
                    errors.confirmPassword.type === 'required' && (
                      <ErrorMessage>Please confirm Password</ErrorMessage>
                    )}
                  {errors.confirmPassword &&
                    errors.confirmPassword.type === 'validate' && (
                      <ErrorMessage>Passwords did not match</ErrorMessage>
                    )}
                </RowItem>
              </Row>
              <Footer>
                <ButtonContainer disabled={saving}>
                  <Button
                    type="submit"
                    variant="primary"
                    size="medium"
                    width="100%">
                    {saving ? 'Signing In...' : 'Save & Sign In'}
                  </Button>
                </ButtonContainer>
                {saving && (
                  <SavingIndicator>
                    <FontAwesomeIcon
                      color={'#fff'}
                      icon={faCircleNotch}
                      size="lg"
                      spin
                    />
                  </SavingIndicator>
                )}
              </Footer>
            </form>
          </InnerContainer>
        )}
      </Container>
    </>
  );
}
